package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/remote")
@Composable
fun RemoteComposePage() {
    BlogLayout(
        PageProperties(
            "Remote Compose",
            ""
        ),
        BlogProperties("Jetpack Compose", "Remote Compose")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Server-Driven UI",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/philipplackner/ContactsComposeMultiplatform/tree/final
            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Server-Driven UI (SDUI) is an architectural pattern where the server controls the structure, content, and behavior of mobile app screens through JSON (or similar formats). Instead of hardcoding UI layouts in the app, the mobile client interprets server responses and dynamically renders components.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Compose Remote",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    androidxRemote = "1.0.0-alpha05"
                    
                    androidx-remote-core = { group = "androidx.compose.remote", name = "remote-core", version.ref = "androidxRemote" }
                    androidx-remote-player-core = { group = "androidx.compose.remote", name = "remote-player-core", version.ref = "androidxRemote" }
                    androidx-remote-player-view = { group = "androidx.compose.remote", name = "remote-player-view", version.ref = "androidxRemote" }
                    
                    // Remote Compose player dependencies (creation happens on server)
                    implementation(libs.androidx.remote.core)
                    implementation(libs.androidx.remote.player.core)
                    implementation(libs.androidx.remote.player.view)
                    """.trimIndent()
            )

            //https://developer.android.com/jetpack/androidx/releases/compose-remote
            KotlinCode(
                code = $$"""
                    data class MainUiState(
                        val documentBytes: ByteArray? = null,
                        val isLoading: Boolean = true,
                        val errorMessage: String? = null,
                        val lastUpdated: Long = 0L,
                    ) {
                        override fun equals(other: Any?): Boolean {
                            if (this === other) return true
                            if (other !is MainUiState) return false
                            return documentBytes.contentEquals(other.documentBytes)
                                && isLoading == other.isLoading
                                && errorMessage == other.errorMessage
                                && lastUpdated == other.lastUpdated
                        }

                        override fun hashCode(): Int {
                            var result = documentBytes?.contentHashCode() ?: 0
                            result = 31 * result + isLoading.hashCode()
                            result = 31 * result + (errorMessage?.hashCode() ?: 0)
                            result = 31 * result + lastUpdated.hashCode()
                            return result
                        }

                        companion object {
                            private const val BASE = ""

                            fun configUrlForScreen(screenId: String): String {
                                return if (screenId == "home") "$BASE/config.rc"
                                else "$BASE/config_$screenId.rc"
                            }
                        }
                    }
                    
                    @Composable
                    fun RemoteDocumentView(
                        documentBytes: ByteArray,
                        modifier: Modifier = Modifier,
                        contentKey: Any = documentBytes.contentHashCode(),
                        onAction: (id: Int, metadata: String?) -> Unit = { _, _ -> },
                    ) {
                        key(contentKey) {
                            AndroidView(
                                factory = { ctx ->
                                    RemoteComposePlayer(ctx).apply {
                                        setDocument(documentBytes)
                                        addIdActionListener { id, metadata ->
                                            onAction(id, metadata)
                                        }
                                    }
                                },
                                modifier = modifier
                            )
                        }
                    }
                    
                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun RemoteScreenTopBar(
                        title: String,
                        subtitle: String? = null,
                        showBack: Boolean,
                        isLoading: Boolean,
                        onBack: () -> Unit,
                        onRefresh: () -> Unit,
                    ) {
                        TopAppBar(
                            title = {
                                Column {
                                    Text(title, fontWeight = FontWeight.ExtraBold)
                                    if (subtitle != null) {
                                        Text(
                                            subtitle,
                                            fontSize = 12.sp,
                                            color = MaterialTheme.colorScheme.onPrimaryContainer.copy(alpha = 0.7f)
                                        )
                                    }
                                }
                            },
                            navigationIcon = {
                                if (showBack) {
                                    IconButton(onClick = onBack) {
                                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
                                    }
                                }
                            },
                            colors = TopAppBarDefaults.topAppBarColors(
                                containerColor = MaterialTheme.colorScheme.primaryContainer,
                                titleContentColor = MaterialTheme.colorScheme.onPrimaryContainer
                            ),
                            actions = {
                                IconButton(
                                    onClick = onRefresh,
                                    enabled = !isLoading
                                ) {
                                    Crossfade(
                                        targetState = isLoading,
                                        label = "refresh"
                                    ) { loading ->
                                        if (loading) {
                                            CircularProgressIndicator(
                                                modifier = Modifier.size(22.dp),
                                                strokeWidth = 2.5.dp,
                                                color = MaterialTheme.colorScheme.onPrimaryContainer
                                            )
                                        } else {
                                            Icon(Icons.Default.Refresh, contentDescription = "Refresh")
                                        }
                                    }
                                }
                            },
                            windowInsets = WindowInsets.statusBars
                        )
                    }
                    
                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun RemoteScreen(
                        configUrl: String,
                        title: String,
                        showBack: Boolean = false,
                        onBack: () -> Unit = {},
                        onNavigate: (String) -> Unit = {},
                    ) {
                        val context = LocalContext.current
                      
                        Scaffold(
                            modifier = Modifier.fillMaxSize(),
                            topBar = {
                                RemoteScreenTopBar(
                                    title = title,
                                    subtitle = subtitle,
                                    showBack = showBack,
                                    isLoading = state.isLoading,
                                    onBack = onBack,
                                    onRefresh = {  }
                                )
                            }
                        ) { innerPadding ->
                            Box(
                                modifier = Modifier
                                    .padding(innerPadding)
                                    .fillMaxSize()
                                    .background(MaterialTheme.colorScheme.surface),
                                contentAlignment = Alignment.Center
                            ) {
                                val errorMessage = state.errorMessage
                                val documentBytes = state.documentBytes

                                when {
                                    state.isLoading && documentBytes == null -> {
                                        CircularProgressIndicator()
                                    }
                                    errorMessage != null && documentBytes == null -> {
                                        ErrorContent(
                                            errorMessage = errorMessage,
                                            onRetry = {  }
                                        )
                                    }
                                    documentBytes != null -> {
                                        RemoteDocumentView(
                                            documentBytes = documentBytes,
                                            modifier = Modifier.fillMaxSize(),
                                            contentKey = state.lastUpdated,
                                            onAction = { id, metadata ->
                                                if (metadata?.startsWith("navigate:") == true) {
                                                   
                                                } else {
                                                    
                                                }
                                            }
                                        )
                                    }
                                }
                            }
                        }
                    }
                    
                    {
                      "backgroundColor": "#FAFAFA",
                      "scrollable": true,
                      "padding": 12,
                      "elements": [
                        {
                          "type": "spacer",
                          "id": "52",
                          "height": 4
                        },
                        {
                          "type": "card",
                          "id": "53",
                          "align": "start",
                          "paddingH": 0,
                          "paddingV": 0,
                          "color": "#FFFFFF",
                          "cornerRadius": 12,
                          "borderColor": "#D0D0D0",
                          "borderWidth": 2,
                          "actionName": "navigate:estimate_detail",
                          "children": [
                            {
                              "type": "card",
                              "id": "54",
                              "align": "start",
                              "paddingH": 14,
                              "paddingV": 6,
                              "color": "#00BCD4",
                              "cornerRadius": 0,
                              "children": [
                                {
                                  "type": "text",
                                  "id": "55",
                                  "text": "Best",
                                  "color": "#FFFFFF",
                                  "fontSize": 13
                                }
                              ]
                            }
                          ]
                        }
                      ]
                    }
                    """.trimIndent()
            )
        }
    }
}

//https://medium.com/@supsabhi/remote-compose-the-future-of-dynamic-android-uis-and-how-it-compares-to-sdui-444a9430de13
//https://github.com/x86xFX/RemoteComposeServer (https://github.com/x86xFX/RemoteComposeClient)
